/**
 * Pipe Tool - BIM 2025 Grade
 * 
 * Comprehensive plumbing/mechanical pipe creation with:
 * - Slope specification (for drainage)
 * - Pressure/flow calculation (Hazen-Williams)
 * - Pipe sizing tables
 * - System types (Domestic, Sanitary, Fire Protection, etc.)
 * - Fitting loss calculation
 * - Insulation specification
 * - Valve placement
 */

import { v4 as uuidv4 } from 'uuid';
import { PipeElement, PipeFitting, Constraint } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { ConnectorManager } from '../core/ConnectorManager';
import { MEPRoutingSolver } from '../core/MEPRoutingSolver';

// ==================== Pipe System Types ====================

export type PipeSystemType =
    // Domestic
    | 'Domestic Cold Water'
    | 'Domestic Hot Water'
    | 'Recirculation Hot Water'
    // Sanitary
    | 'Sanitary'
    | 'Vent'
    | 'Storm'
    // Fire Protection
    | 'Fire Protection Wet'
    | 'Fire Protection Dry'
    | 'Sprinkler'
    // HVAC
    | 'Hydronic Supply'
    | 'Hydronic Return'
    | 'Chilled Water Supply'
    | 'Chilled Water Return'
    | 'Condenser Water'
    // Process
    | 'Compressed Air'
    | 'Natural Gas'
    | 'Medical Gas'
    | 'Other';

// ==================== Pipe Material ====================

export type PipeMaterial =
    | 'Copper Type K'
    | 'Copper Type L'
    | 'Copper Type M'
    | 'PVC Schedule 40'
    | 'PVC Schedule 80'
    | 'CPVC'
    | 'PEX'
    | 'Cast Iron'
    | 'Ductile Iron'
    | 'Carbon Steel Sch 40'
    | 'Carbon Steel Sch 80'
    | 'Stainless Steel'
    | 'HDPE';

export interface PipeMaterialData {
    id: PipeMaterial;
    roughness: number;      // mm (Hazen-Williams C factor)
    cFactor: number;        // Hazen-Williams roughness coefficient
    modulus: number;        // MPa
    maxTemp: number;        // °C
    maxPressure: number;    // kPa
}

export const PIPE_MATERIALS: Record<PipeMaterial, PipeMaterialData> = {
    'Copper Type K': { id: 'Copper Type K', roughness: 0.0015, cFactor: 140, modulus: 117000, maxTemp: 200, maxPressure: 2750 },
    'Copper Type L': { id: 'Copper Type L', roughness: 0.0015, cFactor: 140, modulus: 117000, maxTemp: 200, maxPressure: 2000 },
    'Copper Type M': { id: 'Copper Type M', roughness: 0.0015, cFactor: 140, modulus: 117000, maxTemp: 200, maxPressure: 1400 },
    'PVC Schedule 40': { id: 'PVC Schedule 40', roughness: 0.0015, cFactor: 150, modulus: 2800, maxTemp: 60, maxPressure: 1100 },
    'PVC Schedule 80': { id: 'PVC Schedule 80', roughness: 0.0015, cFactor: 150, modulus: 2800, maxTemp: 60, maxPressure: 1700 },
    'CPVC': { id: 'CPVC', roughness: 0.0015, cFactor: 150, modulus: 2900, maxTemp: 93, maxPressure: 690 },
    'PEX': { id: 'PEX', roughness: 0.001, cFactor: 150, modulus: 900, maxTemp: 95, maxPressure: 690 },
    'Cast Iron': { id: 'Cast Iron', roughness: 0.25, cFactor: 100, modulus: 170000, maxTemp: 350, maxPressure: 1400 },
    'Ductile Iron': { id: 'Ductile Iron', roughness: 0.12, cFactor: 120, modulus: 170000, maxTemp: 350, maxPressure: 2400 },
    'Carbon Steel Sch 40': { id: 'Carbon Steel Sch 40', roughness: 0.045, cFactor: 120, modulus: 200000, maxTemp: 400, maxPressure: 6900 },
    'Carbon Steel Sch 80': { id: 'Carbon Steel Sch 80', roughness: 0.045, cFactor: 120, modulus: 200000, maxTemp: 400, maxPressure: 10300 },
    'Stainless Steel': { id: 'Stainless Steel', roughness: 0.015, cFactor: 140, modulus: 193000, maxTemp: 450, maxPressure: 10300 },
    'HDPE': { id: 'HDPE', roughness: 0.0015, cFactor: 150, modulus: 800, maxTemp: 60, maxPressure: 1100 },
};

// ==================== Pipe Sizes ====================

export interface PipeSize {
    nominal: string;        // Nominal size (e.g., "1/2\"", "15mm")
    outerDiameter: number;  // mm
    wallThickness: number;  // mm
    innerDiameter: number;  // mm
}

// Standard copper pipe sizes (mm)
export const COPPER_PIPE_SIZES: PipeSize[] = [
    { nominal: '15mm', outerDiameter: 15, wallThickness: 0.7, innerDiameter: 13.6 },
    { nominal: '22mm', outerDiameter: 22, wallThickness: 0.9, innerDiameter: 20.2 },
    { nominal: '28mm', outerDiameter: 28, wallThickness: 0.9, innerDiameter: 26.2 },
    { nominal: '35mm', outerDiameter: 35, wallThickness: 1.2, innerDiameter: 32.6 },
    { nominal: '42mm', outerDiameter: 42, wallThickness: 1.2, innerDiameter: 39.6 },
    { nominal: '54mm', outerDiameter: 54, wallThickness: 1.5, innerDiameter: 51.0 },
    { nominal: '67mm', outerDiameter: 67, wallThickness: 1.5, innerDiameter: 64.0 },
    { nominal: '76mm', outerDiameter: 76, wallThickness: 1.5, innerDiameter: 73.0 },
    { nominal: '108mm', outerDiameter: 108, wallThickness: 2.0, innerDiameter: 104.0 },
];

// Standard PVC pipe sizes (mm)
export const PVC_PIPE_SIZES: PipeSize[] = [
    { nominal: '25mm', outerDiameter: 25, wallThickness: 1.9, innerDiameter: 21.2 },
    { nominal: '32mm', outerDiameter: 32, wallThickness: 2.4, innerDiameter: 27.2 },
    { nominal: '40mm', outerDiameter: 40, wallThickness: 3.0, innerDiameter: 34.0 },
    { nominal: '50mm', outerDiameter: 50, wallThickness: 3.7, innerDiameter: 42.6 },
    { nominal: '65mm', outerDiameter: 65, wallThickness: 4.5, innerDiameter: 56.0 },
    { nominal: '80mm', outerDiameter: 80, wallThickness: 5.5, innerDiameter: 69.0 },
    { nominal: '100mm', outerDiameter: 100, wallThickness: 6.9, innerDiameter: 86.2 },
    { nominal: '150mm', outerDiameter: 150, wallThickness: 9.0, innerDiameter: 132.0 },
    { nominal: '200mm', outerDiameter: 200, wallThickness: 11.4, innerDiameter: 177.2 },
];

// ==================== Fitting Types ====================

export type PipeFittingType =
    | 'Elbow 90'
    | 'Elbow 45'
    | 'Elbow Long Radius'
    | 'Tee'
    | 'Tee Branch'
    | 'Cross'
    | 'Reducer'
    | 'Cap'
    | 'Coupling'
    | 'Union'
    | 'Gate Valve'
    | 'Globe Valve'
    | 'Ball Valve'
    | 'Check Valve'
    | 'Butterfly Valve'
    | 'PRV';

// Equivalent length for fittings (in pipe diameters)
export const FITTING_EQUIVALENT_LENGTHS: Record<PipeFittingType, number> = {
    'Elbow 90': 30,
    'Elbow 45': 16,
    'Elbow Long Radius': 20,
    'Tee': 60,
    'Tee Branch': 90,
    'Cross': 90,
    'Reducer': 10,
    'Cap': 0,
    'Coupling': 0,
    'Union': 0,
    'Gate Valve': 8,
    'Globe Valve': 340,
    'Ball Valve': 3,
    'Check Valve': 100,
    'Butterfly Valve': 40,
    'PRV': 200,
};

// ==================== Insulation ====================

export interface PipeInsulation {
    type: 'None' | 'Fiberglass' | 'Foam' | 'Mineral Wool' | 'Armaflex';
    thickness: number;      // mm
    rValue: number;         // Thermal resistance
    vaporBarrier: boolean;
}

// ==================== Enhanced Pipe Fitting ====================

export interface EnhancedPipeFitting extends PipeFitting {
    fittingType: PipeFittingType;
    equivalentLength: number;   // Equivalent pipe length in meters
    pressureDrop: number;       // kPa
    kFactor: number;            // Loss coefficient
}

// ==================== Enhanced Pipe Geometry ====================

export interface EnhancedPipeGeometryData {
    // Route path
    path: [number, number, number][];

    // Size
    nominalSize: string;
    diameter: number;           // mm (outer diameter - for base compatibility)
    outerDiameter: number;      // mm
    innerDiameter: number;      // mm
    wallThickness: number;      // mm

    // Slope (for drainage)
    slope: number;              // percent (e.g., 2 = 2%)
    slopeDirection: 'WithFlow' | 'AgainstFlow' | 'None';

    // Level reference
    referenceLevelId: string;
    offset: number;             // mm

    // Material and insulation
    material: PipeMaterial;
    insulation: PipeInsulation;

    // Fittings
    fittings: EnhancedPipeFitting[];

    // Calculated values
    length: number;             // m
    surfaceArea: number;        // m²
    volume: number;             // L
    weight: number;             // kg (pipe only, not fluid)
}

// ==================== Enhanced Pipe Element ====================

export interface EnhancedPipeElement extends PipeElement {
    geometryData: EnhancedPipeGeometryData;

    // System membership
    systemId?: string;
    systemType: PipeSystemType;

    // Flow data
    flow: {
        designFlow: number;     // L/s
        velocity: number;       // m/s
        maxVelocity: number;    // m/s
        reynoldsNumber: number;
        flowRegime: 'Laminar' | 'Transition' | 'Turbulent';
    };

    // Pressure data
    pressure: {
        designPressure: number;     // kPa
        frictionLoss: number;       // kPa per meter
        totalFrictionLoss: number;  // kPa
        fittingLoss: number;        // kPa
        elevationChange: number;    // kPa (hydrostatic)
        totalDrop: number;          // kPa
    };

    // Temperature (for hot water, steam, etc.)
    temperature?: {
        design: number;             // °C
        maxOperating: number;       // °C
    };

    // Connectors
    connectors: {
        start: PipeConnector;
        end: PipeConnector;
    };
}

export interface PipeConnector {
    id: string;
    position: [number, number, number];
    direction: [number, number, number];
    diameter: number;           // mm (inner)
    connectedTo?: string;       // Element ID
    flow: 'In' | 'Out';
}

// ==================== Tool Options ====================

export interface PipeToolOptions {
    // Size
    nominalSize: string;
    material: PipeMaterial;

    // System
    systemType: PipeSystemType;

    // Flow parameters
    designFlow: number;         // L/s
    designPressure: number;     // kPa
    designTemperature: number;  // °C
    maxVelocity: number;        // m/s

    // Slope (for drainage systems)
    slope: number;              // percent
    slopeDirection: 'WithFlow' | 'AgainstFlow' | 'None';

    // Insulation
    insulation: PipeInsulation;

    // Level
    referenceLevelId: string;
    offset: number;             // mm

    // Chain mode
    chainMode: boolean;
    autoFittings: boolean;
}

// ==================== Pipe Tool Class ====================

export class PipeTool extends BaseTool {
    id = 'pipe';
    name = 'Pipe';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private lastCreatedPipe: EnhancedPipeElement | null = null;

    private options: PipeToolOptions = this.getDefaultOptions();

    private getDefaultOptions(): PipeToolOptions {
        return {
            nominalSize: '22mm',
            material: 'Copper Type L',
            systemType: 'Domestic Cold Water',
            designFlow: 0.5,            // L/s
            designPressure: 350,        // kPa
            designTemperature: 15,      // °C
            maxVelocity: 2.0,           // m/s
            slope: 0,
            slopeDirection: 'None',
            insulation: {
                type: 'None',
                thickness: 0,
                rValue: 0,
                vaporBarrier: false
            },
            referenceLevelId: 'Level 1',
            offset: 2500,               // 2.5m above level
            chainMode: true,
            autoFittings: true
        };
    }

    /**
     * Set pipe options from UI
     */
    setOptions(options: Partial<PipeToolOptions>): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Get current options
     */
    getOptions(): PipeToolOptions {
        return { ...this.options };
    }

    /**
     * Auto-size pipe based on flow
     */
    autoSize(flowLps: number): string {
        const maxVelocity = this.options.maxVelocity;
        const areaRequired = flowLps / (maxVelocity * 1000); // m²
        const diameterRequired = Math.sqrt(4 * areaRequired / Math.PI) * 1000; // mm

        // Find suitable size
        const sizes = this.getPipeSizes();
        for (const size of sizes) {
            if (size.innerDiameter >= diameterRequired) {
                return size.nominal;
            }
        }

        return sizes[sizes.length - 1].nominal; // Return largest
    }

    /**
     * Get pipe sizes for current material
     */
    getPipeSizes(): PipeSize[] {
        if (this.options.material.includes('PVC') || this.options.material.includes('CPVC')) {
            return PVC_PIPE_SIZES;
        }
        return COPPER_PIPE_SIZES; // Default to copper
    }

    private getPipeSize(): PipeSize | undefined {
        return this.getPipeSizes().find(s => s.nominal === this.options.nominalSize);
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        let snapped = this.snapToGrid(point);

        // Snap to connectors
        const elements = Array.from(this.context.bimStore.project.elements.values());
        const connector = ConnectorManager.findClosestConnector(point, elements);
        if (connector) {
            snapped = connector.position.clone();
        }

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.currentPoint = snapped;
        } else {
            if (snapped.distanceTo(this.startPoint) < 0.01) return;

            // Apply slope if drainage system
            if (this.options.slope > 0 && this.options.slopeDirection !== 'None') {
                snapped = this.applySlopeToEndPoint(this.startPoint, snapped);
            }

            const newPipe = this.createPipe(this.startPoint, snapped);

            if (newPipe) {
                this.context.bimStore.addElement(newPipe);

                // Auto-create fitting if needed
                if (this.lastCreatedPipe && this.options.autoFittings) {
                    const fitting = this.createFitting(this.lastCreatedPipe, newPipe, this.startPoint);
                    if (fitting) {
                        this.context.bimStore.addElement(fitting);
                    }
                }

                this.lastCreatedPipe = newPipe;
            }

            if (this.options.chainMode) {
                this.startPoint = snapped;
            } else {
                this.startPoint = null;
                this.currentPoint = null;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            let snapped = this.snapToGrid(point);

            const elements = Array.from(this.context.bimStore.project.elements.values());
            const connector = ConnectorManager.findClosestConnector(point, elements);
            if (connector) {
                snapped = connector.position.clone();
            }

            // Apply slope for preview
            if (this.startPoint && this.options.slope > 0 && this.options.slopeDirection !== 'None') {
                snapped = this.applySlopeToEndPoint(this.startPoint, snapped);
            }

            this.currentPoint = snapped;
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            if (this.startPoint) {
                this.startPoint = null;
                this.currentPoint = null;
                this.lastCreatedPipe = null;
                this.context?.appStore.setToolPreview(null);
            } else {
                super.onKeyDown(event);
            }
        } else if (event.key === 'Enter') {
            this.startPoint = null;
            this.currentPoint = null;
            this.lastCreatedPipe = null;
            this.context?.appStore.setToolPreview(null);
        }
    }

    private applySlopeToEndPoint(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3 {
        const horizontalDist = Math.sqrt(
            Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
        );

        const slopeDrop = horizontalDist * (this.options.slope / 100);
        const adjustedEnd = end.clone();

        if (this.options.slopeDirection === 'WithFlow') {
            adjustedEnd.z = start.z - slopeDrop;
        } else if (this.options.slopeDirection === 'AgainstFlow') {
            adjustedEnd.z = start.z + slopeDrop;
        }

        return adjustedEnd;
    }

    private updatePreview(): void {
        if (!this.context) return;

        if (this.startPoint && this.currentPoint) {
            const pipeSize = this.getPipeSize();
            const length = this.startPoint.distanceTo(this.currentPoint);
            const pressureDrop = this.calculatePressureDrop(length, pipeSize?.innerDiameter || 20);

            this.context.appStore.setToolPreview({
                type: 'pipe',
                start: this.startPoint,
                end: this.currentPoint,
                diameter: (pipeSize?.outerDiameter || 22) / 1000,
                systemType: this.options.systemType,
                slope: this.options.slope,
                pressureDrop: pressureDrop.toFixed(1) + ' kPa'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private createPipe(start: THREE.Vector3, end: THREE.Vector3): EnhancedPipeElement | null {
        if (!this.context) return null;

        const pipeSize = this.getPipeSize();
        if (!pipeSize) return null;

        const length = start.distanceTo(end);
        if (length < 0.01) return null;

        // Calculate hydraulic values
        const area = Math.PI * Math.pow(pipeSize.innerDiameter / 2000, 2); // m²
        const velocity = this.options.designFlow / (area * 1000); // m/s
        const reynolds = this.calculateReynoldsNumber(velocity, pipeSize.innerDiameter);
        const flowRegime = reynolds < 2300 ? 'Laminar' : (reynolds < 4000 ? 'Transition' : 'Turbulent');

        const frictionLoss = this.calculateFrictionLossPerMeter(velocity, pipeSize.innerDiameter);
        const totalFrictionLoss = frictionLoss * length;
        const elevationChange = this.calculateElevationPressure(start, end);
        const totalDrop = totalFrictionLoss + elevationChange;

        const volume = area * length * 1000; // Liters
        const surfaceArea = Math.PI * (pipeSize.outerDiameter / 1000) * length;
        const weight = this.calculateWeight(pipeSize, length);

        // Create connectors
        const dir = end.clone().sub(start).normalize();
        const startConnector: PipeConnector = {
            id: uuidv4(),
            position: start.toArray() as [number, number, number],
            direction: dir.toArray() as [number, number, number],
            diameter: pipeSize.innerDiameter,
            flow: 'In'
        };

        const endConnector: PipeConnector = {
            id: uuidv4(),
            position: end.toArray() as [number, number, number],
            direction: dir.clone().negate().toArray() as [number, number, number],
            diameter: pipeSize.innerDiameter,
            flow: 'Out'
        };

        const geometryData: EnhancedPipeGeometryData = {
            path: [start.toArray() as [number, number, number], end.toArray() as [number, number, number]],
            nominalSize: pipeSize.nominal,
            diameter: pipeSize.outerDiameter,
            outerDiameter: pipeSize.outerDiameter,
            innerDiameter: pipeSize.innerDiameter,
            wallThickness: pipeSize.wallThickness,
            slope: this.options.slope,
            slopeDirection: this.options.slopeDirection,
            referenceLevelId: this.options.referenceLevelId,
            offset: this.options.offset,
            material: this.options.material,
            insulation: { ...this.options.insulation },
            fittings: [],
            length,
            surfaceArea,
            volume,
            weight
        };

        const pipe: EnhancedPipeElement = {
            id: uuidv4(),
            category: 'Pipes',
            type: `${this.options.material} - ${pipeSize.nominal}`,
            parameters: this.buildParameters(geometryData, velocity, frictionLoss, totalDrop),
            geometryData,
            constraints: this.buildConstraints(),
            systemType: this.options.systemType,
            flow: {
                designFlow: this.options.designFlow,
                velocity: Math.round(velocity * 100) / 100,
                maxVelocity: this.options.maxVelocity,
                reynoldsNumber: Math.round(reynolds),
                flowRegime
            },
            pressure: {
                designPressure: this.options.designPressure,
                frictionLoss: Math.round(frictionLoss * 1000) / 1000,
                totalFrictionLoss: Math.round(totalFrictionLoss * 10) / 10,
                fittingLoss: 0, // Calculated when fittings are added
                elevationChange: Math.round(elevationChange * 10) / 10,
                totalDrop: Math.round(totalDrop * 10) / 10
            },
            temperature: this.options.designTemperature > 0 ? {
                design: this.options.designTemperature,
                maxOperating: this.options.designTemperature + 10
            } : undefined,
            connectors: {
                start: startConnector,
                end: endConnector
            }
        };

        return pipe;
    }

    private calculateReynoldsNumber(velocity: number, diameter: number): number {
        // Re = ρ * v * D / μ
        // For water at 15°C: ρ = 999 kg/m³, μ = 1.14e-3 Pa·s
        const rho = 999;
        const mu = 1.14e-3;
        const d = diameter / 1000; // Convert to meters

        return (rho * velocity * d) / mu;
    }

    private calculateFrictionLossPerMeter(velocity: number, diameter: number): number {
        // Hazen-Williams formula: hf = 10.67 * L * Q^1.852 / (C^1.852 * d^4.87)
        // Simplified per meter: hf/L (kPa/m)

        const material = PIPE_MATERIALS[this.options.material];
        const d = diameter / 1000; // meters

        // Using Darcy-Weisbach instead for more accuracy
        const reynolds = this.calculateReynoldsNumber(velocity, diameter);
        const roughness = material.roughness / 1000; // meters

        // Swamee-Jain approximation for friction factor
        const term1 = roughness / (3.7 * d);
        const term2 = 5.74 / Math.pow(reynolds, 0.9);
        const f = 0.25 / Math.pow(Math.log10(term1 + term2), 2);

        // Darcy-Weisbach: ΔP = f * (L/D) * (ρv²/2)
        const rho = 999; // kg/m³ for water
        const pressureLossPerMeter = f * (1 / d) * (rho * velocity * velocity / 2) / 1000; // kPa/m

        return pressureLossPerMeter;
    }

    private calculatePressureDrop(length: number, diameter: number): number {
        const area = Math.PI * Math.pow(diameter / 2000, 2);
        const velocity = this.options.designFlow / (area * 1000);
        const frictionLoss = this.calculateFrictionLossPerMeter(velocity, diameter);
        return frictionLoss * length;
    }

    private calculateElevationPressure(start: THREE.Vector3, end: THREE.Vector3): number {
        // ΔP = ρgh (in kPa)
        const rho = 999; // kg/m³
        const g = 9.81; // m/s²
        const deltaZ = end.z - start.z; // meters

        return (rho * g * deltaZ) / 1000; // kPa
    }

    private calculateWeight(pipeSize: PipeSize, length: number): number {
        // Density based on material type
        let density = 8900; // Copper default
        if (this.options.material.includes('PVC')) density = 1400;
        else if (this.options.material.includes('Steel')) density = 7850;
        else if (this.options.material.includes('Iron')) density = 7200;

        // Cross-sectional area of pipe wall
        const outerArea = Math.PI * Math.pow(pipeSize.outerDiameter / 2000, 2);
        const innerArea = Math.PI * Math.pow(pipeSize.innerDiameter / 2000, 2);
        const wallArea = outerArea - innerArea;

        return wallArea * length * density;
    }

    private createFitting(pipe1: EnhancedPipeElement, pipe2: EnhancedPipeElement, connectionPoint: THREE.Vector3): EnhancedPipeElement | null {
        const dir1 = new THREE.Vector3(...pipe1.connectors.end.direction);
        const dir2 = new THREE.Vector3(...pipe2.connectors.start.direction);

        const angle = Math.acos(Math.abs(dir1.dot(dir2))) * (180 / Math.PI);

        if (angle < 5) return null; // Nearly straight

        const fitting = MEPRoutingSolver.createElbow(pipe1 as any, pipe2 as any, connectionPoint);
        return fitting as EnhancedPipeElement | null;
    }

    private buildParameters(
        geom: EnhancedPipeGeometryData,
        velocity: number,
        frictionLoss: number,
        totalDrop: number
    ): Record<string, any> {
        return {
            // Dimensions
            'Nominal Size': geom.nominalSize,
            'Outer Diameter': geom.outerDiameter,
            'Inner Diameter': geom.innerDiameter,
            'Wall Thickness': geom.wallThickness,
            'Length': Math.round(geom.length * 1000) / 1000,

            // Slope
            'Slope': geom.slope,
            'Slope Direction': geom.slopeDirection,

            // Flow
            'Flow': this.options.designFlow,
            'Velocity': Math.round(velocity * 100) / 100,

            // Pressure
            'Design Pressure': this.options.designPressure,
            'Friction Loss': Math.round(frictionLoss * 1000) / 1000,
            'Total Pressure Drop': Math.round(totalDrop * 10) / 10,

            // Physical
            'System Type': this.options.systemType,
            'Material': geom.material,
            'Insulation': geom.insulation.type,
            'Volume': Math.round(geom.volume * 10) / 10,
            'Weight': Math.round(geom.weight * 10) / 10,

            // Level
            'Reference Level': geom.referenceLevelId,
            'Offset': geom.offset
        };
    }

    protected buildConstraints(): Constraint[] {
        return [
            {
                type: 'LEVEL_BASED',
                targetId: this.options.referenceLevelId,
                data: {
                    offset: this.options.offset
                }
            }
        ];
    }
}

// ==================== Helper Functions ====================

/**
 * Calculate fixture units for drainage sizing
 */
export function calculateDrainageFlow(fixtureUnits: number): number {
    // Hunter's curve approximation
    if (fixtureUnits <= 0) return 0;
    return 0.177 * Math.pow(fixtureUnits, 0.5); // L/s
}

/**
 * Get recommended pipe slope for drainage
 */
export function getRecommendedSlope(diameter: number): number {
    if (diameter <= 75) return 2.0; // 2% for small pipes
    if (diameter <= 100) return 1.5; // 1.5% for medium
    return 1.0; // 1% for large pipes
}
